import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Typography, Space, message, Select } from 'antd';
import { LockOutlined, LoginOutlined, UserOutlined, GlobalOutlined } from '@ant-design/icons';
import { analyticsLogin } from '../../shared/services/analyticsApi';
import { AnalyticsI18nProvider, useI18n } from './i18n';

const { Title, Text } = Typography;

const AuthInner = () => {
    const [id, setId] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const { t, lang, setLang } = useI18n();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleLogin = async () => {
        if (!id.trim() || !password.trim()) {
            message.error(t('error_required'));
            return;
        }

        setLoading(true);
        try {
            await analyticsLogin({ id, password, env: 'production' });
            sessionStorage.setItem('analytics_auth', 'true');
            message.success(t('login_success'));
            window.location.href = '/analytics';
        } catch (error) {
            const msg = error?.response?.data?.message || t('login_error_generic');
            message.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '20px 16px' : '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: isMobile ? '100%' : '400px',
                    borderRadius: '16px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    border: 'none',
                    padding: isMobile ? '32px 20px' : '40px 32px',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Select
                        size={isMobile ? 'small' : 'middle'}
                        value={lang}
                        onChange={setLang}
                        options={[
                            { value: 'ko', label: <span>한국어</span> },
                            { value: 'ja', label: <span>日本語</span> },
                        ]}
                        style={{ width: 110 }}
                        suffixIcon={<GlobalOutlined />}
                    />
                </div>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px auto',
                    }}>
                        <LockOutlined style={{ fontSize: '28px', color: 'white' }} />
                    </div>
                    <Title level={3} style={{ margin: '0 0 8px 0', color: '#1a1a1a' }}>
                        {t('login_title')}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '14px'}}>
                        {t('login_subtitle')}
                    </Text>
                </div>

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                            {t('id_label')}
                        </Text>
                        <Input
                            size="large"
                            placeholder={t('id_placeholder')}
                            value={id}
                            onChange={(e) => setId(e.target.value)}
                            onPressEnter={() => handleLogin()}
                            prefix={<UserOutlined style={{ color: '#8c8c8c' }} />}
                            style={{
                                borderRadius: '8px',
                                fontSize: isMobile ? '16px' : '14px',
                                marginBottom: 12,
                            }}
                        />
                        <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                            {t('password_label')}
                        </Text>
                        <Input.Password
                            size="large"
                            placeholder={t('password_placeholder')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onPressEnter={() => handleLogin()}
                            prefix={<LockOutlined style={{ color: '#8c8c8c' }} />}
                            style={{
                                borderRadius: '8px',
                                fontSize: isMobile ? '16px' : '14px',
                            }}
                        />
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        block
                        loading={loading}
                        onClick={() => handleLogin()}
                        icon={<LoginOutlined />}
                        style={{
                            borderRadius: '8px',
                            height: isMobile ? '48px' : '40px',
                            fontSize: isMobile ? '16px' : '14px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                        }}
                    >
                        {t('login_button')}
                    </Button>
                </Space>
            </Card>
        </div>
    );
};

const Auth = () => (
    <AnalyticsI18nProvider>
        <AuthInner />
    </AnalyticsI18nProvider>
);

export default Auth;